<template>
  <Dialog
    :visible="visible"
    modal
    dismissable-mask
    :style="{ width: 'min(440px, 92vw)' }"
    :pt="{
      root: { class: 'dialog-custom-chrome' },
    }"
    @update:visible="onVisibleChange"
  >
    <template #header>
      <div class="dialog-custom-header">
        <span class="common-confirm-dialog__title">{{ header }}</span>
        <button
          type="button"
          class="dialog-custom-close"
          :aria-label="closeLabel"
          @click="handleReject('close')"
        >
          <i class="pi pi-times" aria-hidden="true" />
        </button>
      </div>
    </template>

    <div class="common-confirm-dialog__body">
      <span
        v-if="icon"
        class="common-confirm-dialog__icon"
        :class="[icon, severity === 'danger' ? 'is-danger' : 'is-primary']"
        aria-hidden="true"
      />
      <p class="common-confirm-dialog__message">{{ message }}</p>
    </div>

    <template #footer>
      <button
        v-if="showReject"
        type="button"
        class="common-confirm-dialog__btn common-confirm-dialog__btn--reject"
        @click="handleReject('button')"
      >
        {{ rejectLabel }}
      </button>
      <button
        type="button"
        class="common-confirm-dialog__btn common-confirm-dialog__btn--accept"
        :class="[
          severity === 'danger' ? 'is-danger' : 'is-primary',
          { 'is-disabled': acceptDisabled },
        ]"
        :disabled="acceptDisabled"
        @click="handleAccept"
      >
        {{ acceptLabel }}
      </button>
    </template>
  </Dialog>
</template>

<script setup>
import Dialog from "primevue/dialog";
import { ref } from "vue";
import "@/components/result/styles/dialogCustomChrome.shared.css";

const props = defineProps({
  visible: { type: Boolean, default: false },
  header: { type: String, default: "" },
  message: { type: String, default: "" },
  icon: { type: String, default: "" },
  acceptLabel: { type: String, default: "确认" },
  rejectLabel: { type: String, default: "取消" },
  closeLabel: { type: String, default: "关闭" },
  acceptDisabled: { type: Boolean, default: false },
  showReject: { type: Boolean, default: true },
  severity: {
    type: String,
    default: "primary",
    validator: (v) => ["primary", "danger"].includes(v),
  },
});

const emit = defineEmits(["update:visible", "accept", "reject"]);

// 记录本次关闭是否已由 accept/reject 按钮显式处理，
// 避免 mask/esc 关闭时与按钮点击重复触发 reject。
const actionHandled = ref(false);

function handleAccept() {
  if (props.acceptDisabled) return;
  actionHandled.value = true;
  emit("accept");
  emit("update:visible", false);
}

function handleReject(source = "close") {
  actionHandled.value = true;
  emit("reject", source);
  emit("update:visible", false);
}

function onVisibleChange(val) {
  if (val) {
    actionHandled.value = false;
    return;
  }
  // 通过点击遮罩 / Esc 关闭：等价于取消
  if (!actionHandled.value) {
    actionHandled.value = true;
    emit("reject", "dismiss");
  }
}
</script>

<style scoped>
.common-confirm-dialog__title {
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  background-image: linear-gradient(
    to right,
    var(--theme-gradient-start),
    var(--theme-gradient-end)
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}

.common-confirm-dialog__body {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  color: var(--text-secondary);
}

.common-confirm-dialog__icon {
  flex: none;
  display: inline-grid;
  place-items: center;
  width: 2.25rem;
  height: 2.25rem;
  font-size: 1rem;
  border-radius: 999px;
  border: 1px solid transparent;
}

.common-confirm-dialog__icon.is-danger {
  color: var(--danger);
  background: color-mix(in srgb, var(--danger) 14%, var(--surface-card));
  border-color: color-mix(in srgb, var(--danger) 22%, transparent);
}

.common-confirm-dialog__icon.is-primary {
  color: var(--theme-primary);
  background: var(--theme-primary-soft-bg);
  border-color: color-mix(in srgb, var(--theme-primary) 22%, transparent);
}

.common-confirm-dialog__message {
  margin: 0;
  line-height: 1.55;
  font-size: 0.9rem;
}

.common-confirm-dialog__btn {
  /* 0-D：为 ::after 命中区提供定位上下文 */
  position: relative;
  min-width: 84px;
  padding: 0.55rem 1rem;
  border-radius: 12px;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  transition: color 0.2s ease, border-color 0.2s ease,
    background-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
}

/* 0-D：视觉尺寸不变，用 ::after 把命中区高度补到 ≥44px（P1-6 / P-5）。
   实测高度 ≈35px（padding .55rem + .88rem 行高），上下各扩 5px ⇒ 45px。
   ⚠️ **只扩纵向、不扩横向**：footer 里 reject / accept 两按钮横向相邻（gap 0.5rem = 8px），
   横向扩张会让两者命中区重叠、边界点击命中错按钮；而 min-width 84px 本就远超 44px，无需横扩。
   ⚠️ 本组件与 BillingNoticeModal 共用，改动需一并回归。 */
.common-confirm-dialog__btn::after {
  content: "";
  position: absolute;
  top: -5px;
  right: 0;
  bottom: -5px;
  left: 0;
}

.common-confirm-dialog__btn--reject {
  background: color-mix(in srgb, var(--zinc-500) 8%, var(--surface-card));
  border-color: color-mix(in srgb, var(--zinc-500) 20%, transparent);
  color: var(--text-secondary);
}

.common-confirm-dialog__btn--reject:hover {
  background: var(--surface-card-hover);
}

.common-confirm-dialog__btn--accept.is-primary {
  color: var(--text-inverse);
  background-image: linear-gradient(
    to right,
    var(--theme-gradient-start),
    var(--theme-gradient-end)
  );
  box-shadow: 0 6px 14px color-mix(in srgb, var(--theme-primary) 26%, transparent);
}

.common-confirm-dialog__btn--accept.is-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 20px color-mix(in srgb, var(--theme-primary) 38%, transparent);
}

.common-confirm-dialog__btn--accept.is-danger {
  color: var(--text-inverse);
  background-image: linear-gradient(120deg, var(--danger-hover), var(--danger));
  box-shadow: 0 6px 14px color-mix(in srgb, var(--danger) 26%, transparent);
}

.common-confirm-dialog__btn--accept.is-danger:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 20px color-mix(in srgb, var(--danger) 38%, transparent);
}

.common-confirm-dialog__btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--theme-focus-ring);
}
</style>
